import { Box } from '@wix/design-system';
import { ProductUtils } from '@wix/package-picker-shared';
import {
  EclipseCta,
  SelectPlanProvider,
  useSwitchFlows,
} from '@wix/package-picker-shared-eclipse';
import { FeaturesGrid as PlatformFeaturesGrid } from '@wix/premium-purchase-platform/@components';
import React from 'react';
import { useDealerIntent } from '../../hooks/useDealerIntent';
import { EclipsePackagePickerModule } from '../../modules/eclipse-package-picker.module';
import { arrangeFeaturesGridNamespaces } from './arrangeFeaturesGridNamespaces';
import { FeaturesGridGroup } from './FeaturesGridGroup';
import { FeaturesGridProductHeader } from './FeaturesGridProductHeader';
import { FeaturesGridSection } from './FeaturesGridSection';

export interface FeaturesGridProps {
  innerRef?: React.RefObject<HTMLDivElement>;
  /** Called when a plan is selected. If provided, replaces default navigation to cycle page. */
  onSelectPlan?: (productId: string) => void;
}

export const FeaturesGrid = ({ innerRef, onSelectPlan }: FeaturesGridProps) => {
  const { t } = EclipsePackagePickerModule.internals.useTranslations();
  const { experiments } =
    EclipsePackagePickerModule.internals.experiments.hooks.useExperiments();
  const isUseDealerIntegrationIntentElementsEnabled = experiments.isEnabled(
    'specs.premium.useDealerIntegrationIntentBasedElements',
  );
  const { isUpgradeFlow } = useSwitchFlows();
  const { intent } = useDealerIntent({
    isUpgradeFlow,
    enabled: isUseDealerIntegrationIntentElementsEnabled,
  });

  return (
    <SelectPlanProvider onSelectPlan={onSelectPlan}>
      <PlatformFeaturesGrid.Root
        innerRef={innerRef}
        sortProducts={(products) => ProductUtils.sortProducts({ products })}
      >
        <PlatformFeaturesGrid.Header
          marginTop={28}
          cornerCellProps={{ border: 'none' }}
        >
          {({ products }) => {
            const sortedProducts = ProductUtils.sortProducts({ products });

            return sortedProducts?.map((product) => {
              if (!product.productId) {
                return null;
              }

              return (
                <FeaturesGridProductHeader
                  key={product.productId}
                  productId={product.productId!}
                />
              );
            });
          }}
        </PlatformFeaturesGrid.Header>
        <PlatformFeaturesGrid.Content>
          {({ products, namespaces }) => {
            const { primaryNamespaces, ternaryNamespaces } =
              arrangeFeaturesGridNamespaces({
                namespaces,
                intent,
                isIntentBasedPromotionEnabled:
                  isUseDealerIntegrationIntentElementsEnabled,
              });

            return (
              <>
                {primaryNamespaces?.map((namespace) => (
                  <FeaturesGridGroup
                    key={namespace.id}
                    group={namespace}
                    products={products}
                  />
                ))}
                {ternaryNamespaces && ternaryNamespaces.length > 0 && (
                  <PlatformFeaturesGrid.Content.Collapsible
                    renderShowMoreButton={({ isOpen, onClick }) => (
                      <Box width="100%" align="center">
                        <EclipseCta
                          variant="secondary"
                          onClick={() => {
                            EclipsePackagePickerModule.internals.bi.onFeaturesGridCollapsibleClick?.(
                              {
                                action: isOpen ? 'close' : 'open',
                              },
                            );
                            onClick();
                          }}
                          width="max-content"
                        >
                          {isOpen
                            ? t('premium-purchase-plan.show_less.CTA')
                            : t('premium-purchase-plan.show_more.CTA')}
                        </EclipseCta>
                      </Box>
                    )}
                  >
                    {ternaryNamespaces.map((namespace) => (
                      <FeaturesGridSection
                        key={namespace.id}
                        group={namespace}
                        products={products}
                      />
                    ))}
                  </PlatformFeaturesGrid.Content.Collapsible>
                )}
              </>
            );
          }}
        </PlatformFeaturesGrid.Content>
      </PlatformFeaturesGrid.Root>
    </SelectPlanProvider>
  );
};
